import { useMyMembership, useRedeemTeamInvite, useRegisterCustomer } from "@hooks/queries"
import { useEffect } from "react"
import { proxy, useSnapshot } from "valtio"
import { useAuth } from "./useAuth"
import { useCartController } from "./useCartController"
import { BusinessTeamInviteFragment } from "../../../types.graphql"
import { displayErrorAlert } from "@hooks/useAlertOverlay"

type SubscribeStore = {
  currentPhase: keyof typeof JoinPhase | null
  isAlreadyUser: boolean
  isRenewal: boolean
  chosenPlanSlug: string | undefined
  hasConfirmedDetails: boolean
  complete: boolean
  createdUser: boolean
  subscriptionId?: number
  invite?: BusinessTeamInviteFragment
  acceptedInvite?: boolean
  inviteState?: InviteState
  redeemingInvite?: boolean
}

type InviteState = "create-account" | "ready" | "swap-account" | "login"

const emptyStore = (): SubscribeStore => ({
  currentPhase: null,
  isAlreadyUser: false,
  isRenewal: false,
  chosenPlanSlug: undefined,
  createdUser: false,
  hasConfirmedDetails: false,
  complete: false,
  subscriptionId: undefined,
  invite: undefined,
  acceptedInvite: false,
  inviteState: undefined,
})

const store = proxy({
  ...emptyStore(),
})

// export const joinStore = create<SubscribeStore>()(
//   persist(
//     (set, get) => ({
//       start(opts) {
//         set({
//           isRenewal: opts.isRenewal,
//           chosenPlanSlug: opts.planSlug,
//         })
//       },
//     }),
//     {
//       name: "join-state",
//     },
//   ),
// )

const JoinPhase = {
  AcceptInvite: {
    label: "Accept Invite",
    header: "Join Team Membership",
    if(params) {
      return !!params.invite
    },
    canProceed(params) {
      return !!params.acceptedInvite
    },
  },
  ChoosePlan: {
    label: "Choose Membership Plan",
    header: "Select membership type:",
    if(params) {
      if (params.invite) return false
      return true
    },
    canProceed(params) {
      return !!params.chosenPlanSlug
    },
  },
  SignIn: {
    label: "Sign In",
    header: "Sign in",
    if(params) {
      return params.inviteState === "login"
    },
    canProceed(params) {
      return params.isLoggedIn
    },
  },
  CreateAccount: {
    label: "Account Setup",
    header: "Account setup",
    if(params) {
      if (params.invite && params.inviteState !== "create-account") return false
      return !params.isAlreadyUser
    },
    canProceed(params) {
      return params.isLoggedIn
    },
  },
  Confirm: {
    label: "Confirm Details",
    header: "Confirm Details",
    if(params) {
      return true && !params.invite
    },
    canProceed(params) {
      return params.isLoggedIn && params.hasConfirmedDetails
    },
  },
  BillingDetails: {
    label: "Billing Details",
    header: "Subscription and billing",
    if(params) {
      if (params.invite) return false
      return true
    },
    canProceed(params) {
      return params.complete
    },
  },
  Complete: {
    label: "Finished",
    header: "Finished",
    if(params) {
      return params.complete
    },
    canProceed(params) {
      return false
    },
  },
} satisfies Record<
  string,
  {
    label: string
    header: string
    if: (params: JoinParams) => boolean
    canProceed: (params: JoinParams) => boolean
  }
>

type JoinParams = SubscribeStore & {
  isLoggedIn: boolean
  alreadyHasMembership: boolean
}

export function useJoinProcedure() {
  const auth = useAuth()
  const membership = useMyMembership(undefined)

  const redeemInvite = useRedeemTeamInvite({
    onSuccess() {
      store.complete = true
    },
    onMutate() {
      store.redeemingInvite = true
    },
    onSettled() {
      store.redeemingInvite = false
      membership.refetch()
    },
    onError(err) {
      displayErrorAlert(err)
    },
  })

  const state = useSnapshot(store)
  const cart = useCartController("subscribe")

  useEffect(() => {
    store.inviteState = !auth.isLoggedIn() ? (store.invite?.user?.id ? "login" : "create-account") : store.invite?.email === auth.user?.email ? "ready" : "swap-account"
  }, [state.invite, state.acceptedInvite, auth.user?.email, auth.isLoggedIn()])

  useEffect(() => {
    cart.setItems(
      state.subscriptionId && auth.isLoggedIn()
        ? [
            {
              productId: state.subscriptionId,
              quantity: 1,
              variationId: undefined,
            },
          ]
        : []
    )
  }, [state.subscriptionId, auth.isLoggedIn()])

  useEffect(() => {
    if (!store.chosenPlanSlug) {
      store.isAlreadyUser = !!membership.data?.customer?.databaseId
    }
  }, [!!membership.data?.customer])

  const onPlan = membership.data?.customer?.latestMembership?.isActive && membership.data.customer.latestMembership.plan
  const expiredPlanSlug = !membership.data?.customer?.latestMembership?.isActive && membership.data?.customer?.latestMembership?.plan?.slug
  store.isRenewal = membership.data?.customer?.latestMembership?.status === "expired" || membership.data?.customer?.latestMembership?.status === "active"

  const isLoggedIn = auth.isLoggedIn()
  const canJoinOrRenew = membership.data?.customer?.latestMembership?.status === "expired" || membership.data?.customer?.latestMembership?.status === "active"
  const mustChoosePlan = !state.chosenPlanSlug

  useEffect(() => {}, [auth.isLoggedIn()])

  const params: JoinParams = {
    ...state,
    isLoggedIn,
    alreadyHasMembership: !!onPlan,
  }

  const phases = Object.entries(JoinPhase)
    .filter(([_, phase]) => phase.if(params))
    .map(([key, phase]) => ({
      key: key as keyof typeof JoinPhase,
      label: phase.label,
      header: phase.header,
      canProceed: phase.canProceed(params),
      canAccess: false,
      active: false,
    }))

  for (let phase of phases) {
    if (!phase.canProceed) {
      phase.canAccess = true
      break
    } else {
      phase.canAccess = true
    }
  }

  const currentPhase = phases.find((phase) => phase.key === state.currentPhase)
  if (currentPhase) {
    currentPhase.active = true
  } else {
    const lastAccessiblePhase = phases.findLast((phase) => phase.canAccess)
    if (lastAccessiblePhase) {
      lastAccessiblePhase.active = true
    }
  }

  useEffect(() => {
    if (state.invite && state.inviteState === "ready" && state.acceptedInvite && redeemInvite.status === "idle") {
      // The user has accepted, and is ready to join
      redeemInvite.mutate({
        hash: state.invite.hash!,
      })
    }
  }, [auth.isLoggedIn(), state.inviteState, state.invite, state.acceptedInvite])

  return {
    ...state,
    phases,
    membership: membership.data,
    expiredPlanSlug,
    currentPhase: phases.find((phase) => phase.active)?.key,
    cart,
    start(planSlug: string, subscriptionId: number) {
      const isRenewal = membership.data?.customer?.latestMembership?.status === "expired" || membership.data?.customer?.latestMembership?.status === "active"
      const isAlreadyUser = !!membership.data?.customer?.databaseId
      Object.assign(store, {
        ...emptyStore(),
        isRenewal,
        isAlreadyUser,
        chosenPlanSlug: planSlug,
        subscriptionId,
        currentPhase: null,
      })
    },
    startFromInvite(invite: BusinessTeamInviteFragment) {
      const isRenewal = membership.data?.customer?.latestMembership?.status === "expired" || membership.data?.customer?.latestMembership?.status === "active"
      const isAlreadyUser = !!membership.data?.customer?.databaseId
      console.log("Starting from invite", invite)
      Object.assign(store, {
        ...emptyStore(),
        isRenewal,
        isAlreadyUser,
        chosenPlanSlug: invite.plan?.slug!,
        currentPhase: null,
        invite,
        subscriptionId: undefined,
      })
    },
    goTo(phase: keyof typeof JoinPhase | null) {
      store.currentPhase = phase
    },
    confirmDetails() {
      store.hasConfirmedDetails = true
    },
    markComplete() {
      store.currentPhase = null
      store.complete = true
      membership.refetch()
    },
    markCreatedUser() {
      store.createdUser = true
    },
    acceptInvite() {
      store.acceptedInvite = true
      store.currentPhase = null
    },
    goBack() {
      const current = phases.find((phase) => phase.active)?.key
      store.currentPhase = phases[phases.findIndex((phase) => phase.key === current) - 1]?.key
    },
  }
}
